import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Plotly from "plotly.js-dist";
import anychart from "anychart";

import { removeStopwordsFromWordCounts, takeRandomSampleWithSeed } from "../utils";

const ExplorationApp = (props) => {
    const [filters, setFilters] = useState({
        target: [],
        implicitness: [],
        hatefulness: [],
        dataset: []
    });
    const [chart, setChart] = useState("sankey");
    const [data, setData] = useState();

    useEffect(() => {
        if (Object.values(filters).every(item => item.length > 0))
            applyFilters();
        else
            setData(null);
    }, [filters]);

    useEffect(() => {
        $("#chart").html("");
        if(data && data.length > 0) {
            if(chart == "sankey") {
                // Best words for each topic (sample)
                // const colBestWords = this.data.forEach(item => item.best_words);
                // const uniqueArray = [...new Set(colBestWords)];
                let bestWords = {};
                data.forEach(item => { bestWords[item.topic] = item.best_words; });
                
                // Group data and count occurrences
                const groupedData = {};
                data.forEach(item => {
                    const key = `${item.target}#${item.implicitness === 'no' ? 'Explicit HS' : 'Implicit HS'}#${item.topic}`;
                    if (!groupedData[key]) {
                        groupedData[key] = 0;
                    }
                    groupedData[key] += 1;
                });
            
                // Prepare data for the Sankey diagram
                let labelList = [];
                let sources = [];
                let targets = [];
                let values = [];
                
                Object.keys(groupedData).forEach(key => {
                    const [target, implicitness, topic] = key.split('#');
                    
                    // const topicWords = bestWords[topic]
                
                    // Ensure all labels are in labelList and have unique indices
                    [target, implicitness, bestWords[topic]].forEach(label => {
                        if (!labelList.includes(label)) {
                            labelList.push(label);
                        }
                    });
                
                    const sourceIndex = labelList.indexOf(target);
                    const targetIndex1 = labelList.indexOf(implicitness);
                    const targetIndex2 = labelList.indexOf(bestWords[topic]);
                
                    sources.push(sourceIndex);
                    targets.push(targetIndex1);
                    values.push(groupedData[key]);
                
                    sources.push(targetIndex1);
                    targets.push(targetIndex2);
                    values.push(groupedData[key]);
                });
            
                // Define colors for nodes
                const colors = ['blue', 'green', 'red', 'purple', 'orange'].concat(['blue', 'green', 'red', 'purple', 'orange']);
            
                const dataTrace = {
                    type: 'sankey',
                    node: {
                        pad: 15,
                        thickness: 20,
                        line: {
                            color: 'black',
                            width: 0.5
                        },
                        label: labelList,
                        color: colors
                    },
                    link: {
                        source: sources,
                        target: targets,
                        value: values
                    }
                };
            
                const layout = {
                    title: 'Hate Speech Sankey Diagram',
                    font: {
                        size: 10
                    }
                };
            
                Plotly.newPlot('chart', [dataTrace], layout);
            } else if(chart == "target-frequency") {
                // Count the number of elements for each target
                const targetCounts = {};
                data.forEach(item => {
                    const target = item.target;
                    targetCounts[target] = (targetCounts[target] || 0) + 1;
                });

                // Extract targets and their counts
                const targets = Object.keys(targetCounts);
                const counts = Object.values(targetCounts);

                // Create trace for bubble chart
                const trace = {
                    x: targets, // Targets
                    y: counts, // Counts
                    mode: 'markers',
                    marker: {
                        size: counts.map(count => Math.sqrt(count) * 10), // Bubble size based on counts
                        sizemode: 'area',
                        sizeref: 0.05,
                        color: 'rgb(31, 119, 180)', // Marker color
                        opacity: 0.5
                    }
                };

                // Create data array containing trace
                const dataToPlot = [trace];

                // Define layout
                const layout = {
                    title: 'Target Frequency Bubble Chart',
                    xaxis: {
                        title: 'Target'
                    },
                    yaxis: {
                        title: 'Frequency'
                    }
                };

                // Plot the bubble chart
                Plotly.newPlot('chart', dataToPlot, layout);
            } else if(chart == "word-cloud") {
                const sampleSize = 5000;
                const seed = 0;
                const randomSample = takeRandomSampleWithSeed(data, sampleSize, seed);
        
                let allText = randomSample.map(item => item.text).join(" ");
        
                // Split the string into words and count occurrences
                let words = allText.toLowerCase().match(/\b(\w+)\b/g);
        
                
                let frequencyCount = {};
                words.forEach(word => {
                    if (!frequencyCount[word]) {
                        frequencyCount[word] = 0;
                    }
                    frequencyCount[word] += 1;
                });
                
                let frequencyCountNoStop = removeStopwordsFromWordCounts(frequencyCount);
                
                // Prepare data for AnyChart
                let chartData = Object.keys(frequencyCountNoStop).map(word => {
                    return { x: word, value: frequencyCountNoStop[word] };
                });
        
                // Create and configure the word cloud chart
                anychart.onDocumentReady(function () {
                    var chart = anychart.tagCloud(chartData);
        
                    chart.title('Word Cloud');
                    chart.angles([0]);
                    chart.colorRange(true);
                    chart.colorRange().length('80%');
                    chart.container('chart');
                    chart.draw();
                });
            }
        } else {

            // Define layout
            const layout = {
                xaxis: { visible: false },
                yaxis: { visible: false },
                annotations: [{
                    text: "No data found",
                    xref: "paper",
                    yref: "paper",
                    showarrow: false,
                    font: { size: 28 }
                }]
            };

            // Plot the bubble chart
            Plotly.newPlot('chart', [], layout, { displayModeBar: false });
        }
    }, [chart, data]);

    const applyFilters = async () => {
        try {
            const response = await fetch(
                props.explorationApiEndpoint,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', },
                    body: JSON.stringify(filters),
                }
            );
            const data = await response.json();

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            setData(data.data);
            console.log('Success:', data);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleUpdateFilters = () => {
        const updatedFilters = {
            target: [...document.querySelectorAll('#target-filters input[type="checkbox"]:checked')].map(checkbox => checkbox.value),
            implicitness: [...document.querySelectorAll('#implicitness-filters input[type="checkbox"]:checked')].map(checkbox => checkbox.value),
            hatefulness: [...document.querySelectorAll('#hatefulness-filters input[type="checkbox"]:checked')].map(checkbox => checkbox.value),
            dataset: [...document.querySelectorAll('#dataset-filters input[type="checkbox"]:checked')].map(checkbox => checkbox.value),
        };
        setFilters(updatedFilters);
    };

    const handleChartChange = (chartType) => { setChart(chartType); };

    return <div className = "row">
        <div className  = "col-lg-3 col-12 text-center">
            <div className = "text-secondary fw-bold">TARGET</div>
            <ul className="list-unstyled" id = "target-filters">
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="ethnic-racial-minorities" value="Ethnic/Racial Minorities" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary custom-btn" htmlFor="ethnic-racial-minorities">Ethnic/Racial Minorities</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="lgbtq-plus" value="LGBTQ+" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="lgbtq-plus">LGBTQ+</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="religious-groups" value="Religious Groups" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="religious-groups">Religious Groups</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="political-affiliations" value="Political Affiliations" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="political-affiliations">Political Affiliations</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="nationalities-regions" value="Nationalities/Regions" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="nationalities-regions">Nationalities/Regions</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="women" value="Women" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="women">Women</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="immigrants-and-refugees" value="Immigrants and Refugees" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="immigrants-and-refugees">Immigrants and Refugees</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="people-with-disabilities" value="People with Disabilities" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="people-with-disabilities">People with Disabilities</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="economic-status" value="Economic Status" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="economic-status">Economic Status</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="professions-and-roles" value="Professions and Roles" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="professions-and-roles">Professions and Roles</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="miscellaneous-identifiable-groups" value="Miscellaneous Identifiable Groups" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="miscellaneous-identifiable-groups">Miscellaneous Identifiable Groups</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="non-specified-or-general-targets" value="Non-Specified or General Targets" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="non-specified-or-general-targets">Non-Specified or General Targets</label>
                </li>
            </ul>
        </div>
        <div className  = "col-lg-6 col-12">
            <div className = "btn-group btn-group-lg mb-3 d-flex justify-content-center flex-wrap" role="group">
                <input type = "radio" className = "btn-check" name = "chart-type" id = "sankey" onClick = { () => { handleChartChange("sankey"); } } defaultChecked = { chart == "sankey" } />
                <label className = "btn btn-primary rounded-start-pill d-none d-lg-inline-block" htmlFor = "sankey">Sankey Diagram</label>
                <label className = "btn btn-primary d-inline-block d-lg-none" htmlFor = "sankey">Sankey Diagram</label>

                <input type = "radio" className = "btn-check" name = "chart-type" id = "target-frequency" onClick = { () => { handleChartChange("target-frequency"); } } defaultChecked = { chart == "target-frequency" } />
                <label className = "btn btn-primary" htmlFor = "target-frequency">Target Frequency</label>

                <input type = "radio" className = "btn-check" name = "chart-type" id = "word-cloud" onClick = { () => { handleChartChange("word-cloud"); } } defaultChecked = { chart == "word-cloud" } />
                <label className = "btn btn-primary rounded-end-pill d-none d-lg-inline-block" htmlFor = "word-cloud">Word Cloud</label>
                <label className = "btn btn-primary d-inline-block d-lg-none" htmlFor = "word-cloud">Word Cloud</label>
            </div>
            <div id = "chart" className = "plot bg-light w-100" style= {{ height: 600 }}></div>
        </div>
        <div className  = "col-lg-3 col-12 text-center">
            <div className = "text-secondary fw-bold">IMPLICITNESS</div>
            <ul className="list-unstyled" id = "implicitness-filters">
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="implicit" value="yes" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="implicit">Implicit</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="explicit" value="no" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="explicit">Explicit</label>
                </li>
            </ul>
            <div className = "text-secondary fw-bold">HATEFULNESS</div>
            <ul className="list-unstyled" id = "hatefulness-filters">
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="hate-speech" value = "hs" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="hate-speech">Hate Speech</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="non-hate-speech" value = "non-hs" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="non-hate-speech">Non-Hate Speech</label>
                </li>
            </ul>
            <div className = "text-secondary fw-bold">DATASET</div>
            <ul className="list-unstyled" id = "dataset-filters">
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="ishate" value="ishate" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="ishate">ISHate</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="ihc" value="ihc" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="ihc">IHC</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="sbic" value="sbic" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="sbic">SBIC</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="dynahate" value="dynahate" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="dynahate">Dynahate</label>
                </li>
                <li className = "my-3">
                    <input type="checkbox" className = "btn-check" id="toxigen" value="toxigen" onChange = { handleUpdateFilters } />
                    <label className = "btn btn-outline-secondary" htmlFor="toxigen">Toxigen</label>
                </li>
            </ul>
        </div>
    </div>;
};

ExplorationApp.propTypes = {
    explorationApiEndpoint: PropTypes.string
};

export default ExplorationApp;
